import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BackNavigable,
  BackNavigationService,
  RefreshService,
  Refreshable,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
  menuItems,
} from '@softline/application';
import { Store } from '@softline/core';
import { UiCoreModule } from '@softline/ui-core';
import { Subscription, combineLatest, map, switchMap } from 'rxjs';
import { SecondsToHoursPipe } from './../../pipes/seconds-to-hours.pipe';
import { DateNavigatorService } from './../../services/date-navigator.service';
import { SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL } from './../../statusabfrage.shared';
import { PmsPersonalDetailStore } from './../../store/personal-detail.store';

@Component({
  selector: 'soft-mitarbeiter-detail',
  imports: [CommonModule, UiCoreModule, SecondsToHoursPipe],
  templateUrl: './mitarbeiter-detail.component.html',
  styleUrls: ['./mitarbeiter-detail.component.scss'],
  providers: [DatePipe],
})
export class MitarbeiterDetailComponent
  implements OnInit, OnDestroy, BackNavigable, Refreshable
{
  readonly expanded = true;
  private mitarbeiterSubscription?: Subscription;
  private subscription?: Subscription;

  private readonly id$ = this.activatedRoute.paramMap.pipe(map((o) => o.get('idlstfix')));

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
    PmsPersonalDetailStore.getters.loading
  );

  readonly mitarbeiter$ = this.store.observe(
    SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
    PmsPersonalDetailStore.getters.data
  );

  readonly mitarbeiterDetail$ = this.id$.pipe(
    switchMap((id) =>
      this.store.observe(
        SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
        PmsPersonalDetailStore.getters.data
      )
    )
  );

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private readonly store: Store,
    private datePipe: DatePipe,
    private readonly dateNavigatorService: DateNavigatorService,
    private readonly backNavigationService: BackNavigationService,
    private readonly refreshService: RefreshService
  ) {
    menuItems(() => [
      {
        name: 'abteilungen',
        type: 'route',
        outlet: 'nav',
        icon: 'fa-regular fa-list',
        title: 'Abteilungen',
        routerLink: ['/statusabfrage'],
      },
      {
        name: 'abteilung',
        type: 'route',
        outlet: 'nav',
        icon: 'fa-regular fa-users',
        title: 'Abteilung',
        routerLink: [
          '/statusabfrage',
          'abteilung',
          this.activatedRoute.snapshot.paramMap.get('id'),
        ],
      },
      {
        name: 'mitarbeiter',
        type: 'route',
        outlet: 'nav',
        icon: 'fa-regular fa-user',
        title: 'Mitarbeiterdetails',
        routerLink: [
          '/statusabfrage',
          'abteilung',
          this.activatedRoute.snapshot.paramMap.get('id'),
          'detail',
          this.activatedRoute.snapshot.paramMap.get('idlstfix'),
        ],
      },
    ]);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['./../../'], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'preserve',
    });
  }

  ngOnInit(): void {
    this.backNavigationService.set(this);
    this.refreshService.add(this);

    this.subscription = combineLatest([
      this.id$,
      this.dateNavigatorService.date$,
    ]).subscribe(async ([id, date]) => {
      await this.store.dispatch(
        SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
        PmsPersonalDetailStore.actions.load,
        {
          pathParams: {
            stichdatum: this.datePipe.transform(date, 'yyyy-MM-dd'),
            idlstfix: id,
          },
        }
      );
    });

    this.mitarbeiterSubscription = this.mitarbeiter$.subscribe((mitarbeiter) => {
      if (!mitarbeiter) {
        this.store.commit(
          SOFTLINE_FEATURE_TITLE,
          TitleStore.mutations.setSubTitle,
          `Lade...`
        );
        return;
      }

      const { persnr, vorname, zuname } = mitarbeiter.lstfix;

      this.store.commit(
        SOFTLINE_FEATURE_TITLE,
        TitleStore.mutations.setSubTitle,
        `${persnr} - ${vorname} ${zuname}`
      );
    });
  }

  ngOnDestroy(): void {
    this.backNavigationService.set(undefined);
    this.refreshService.remove(this);
    this.mitarbeiterSubscription?.unsubscribe();
    this.subscription?.unsubscribe();

    this.store.commit(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
      PmsPersonalDetailStore.mutations.unset
    );

    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setSubTitle, '');
  }

  async refresh(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_PMS_STATUSABFRAGE_ABTEILUNGEN_MITARBEITER_DETAIL,
      PmsPersonalDetailStore.actions.load,
      {
        pathParams: {
          stichdatum: this.datePipe.transform(
            this.dateNavigatorService.date,
            'yyyy-MM-dd'
          ),
          idlstfix: this.activatedRoute.snapshot.paramMap.get('idlstfix'),
        },
      }
    );
  }
}
